import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Typography,
  Chip,
  CircularProgress,
  Tooltip,
  IconButton,
  Stack
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getManagerFormAssignments } from './FormManagementQueries';
import { getAllProbationReviews } from '../ProbationReview/ProbationReviewQueries';
import { getAllReviews as getAllAnnualReviews } from '../AnnualReview/AnnualReviewQueries';
import { API } from 'aws-amplify';

const isCorporateMode = process.env.REACT_APP_CORPORATE_MODE === 'true';
const api_name = isCorporateMode 
    ? "Corporate-API"
    : (process.env.REACT_APP_STAGE === 'DEV' ? "Node-API" : "prod-api");

const ManagerReviewsTable = () => {
  const [forms, setForms] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadManagerForms();
  }, []);

  const loadManagerForms = async () => {
    try {
      setLoading(true);
      
      // Load forms
      const formsResponse = await getManagerFormAssignments();
      setForms(formsResponse || []);
      
      // Load all reviews (both probation and annual)
      const [probationResponse, annualResponse] = await Promise.all([
        getAllProbationReviews(),
        getAllAnnualReviews(),
      ]);
      const probationList = Array.isArray(probationResponse?.reviews) ? probationResponse.reviews
        : Array.isArray(probationResponse?.Items) ? probationResponse.Items
        : Array.isArray(probationResponse) ? probationResponse : [];
      const annualList = Array.isArray(annualResponse?.reviews) ? annualResponse.reviews
        : Array.isArray(annualResponse?.Items) ? annualResponse.Items
        : Array.isArray(annualResponse) ? annualResponse : [];
      setReviews([...probationList, ...annualList]);
    } catch (error) {
      console.error('Error loading manager forms:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleDateString('en-US', { month: 'long' });
    const year = date.getFullYear();
    
    const suffix = day === 1 || day === 21 || day === 31 ? 'st' :
                   day === 2 || day === 22 ? 'nd' :
                   day === 3 || day === 23 ? 'rd' : 'th';
    
    return `${month} ${day}${suffix} ${year}`;
  };

  const getStatusChip = (status) => {
    const statusColors = {
      draft: 'default',
      active: 'success',
      closed: 'error',
      archived: 'warning'
    };

    return (
      <Chip 
        label={status?.toUpperCase() || 'DRAFT'} 
        color={statusColors[status?.toLowerCase()] || 'default'} 
        size="small" 
      />
    );
  };

  const unwrap = (v) => (v && typeof v === 'object' && 'S' in v ? v.S : v);

  const findReview = (form) => {
    return reviews.find(r => {
      const rEmail = String(unwrap(r.email) || '').toLowerCase().trim();
      const rPeriodStart = unwrap(r.period_start) || '';
      const rId = unwrap(r.id) || '';
      const empEmail = String(form.employee_email || '').toLowerCase().trim();
      const emailMatch = rEmail === empEmail && rPeriodStart === form.start_date;
      const idMatch = rId === `${empEmail}_probation_${form.start_date}` ||
                      rId === `${empEmail}_annual_${form.start_date}`;
      return emailMatch || idMatch;
    });
  };

  const isEmployeeSubmitted = (form) => {
    const review = findReview(form);
    if (review) {
      const userResp = unwrap(review.user_response);
      return !!userResp;
    }
    return !!form.employee_submitted;
  };

  const isManagerSubmitted = (form) => {
    const review = findReview(form);
    if (review) {
      const managerResp = unwrap(review.manager_response);
      return !!managerResp;
    }
    return !!form.manager_submitted;
  };

  const handleOpenForm = (form) => {
    // Parse form_schema if it's a string
    let parsedSchema = form.form_schema;
    if (typeof form.form_schema === 'string') {
      try {
        parsedSchema = JSON.parse(form.form_schema);
      } catch (error) {
        console.error('Error parsing form schema:', error);
      }
    }

    // Parse assigned_employees to get employee details
    let assignedEmployees = [];
    if (typeof form.assigned_employees === 'string') {
      try {
        assignedEmployees = JSON.parse(form.assigned_employees);
      } catch (error) {
        console.error('Error parsing assigned employees:', error);
      }
    } else {
      assignedEmployees = form.assigned_employees || [];
    }

    // Find the employee for this specific assignment
    const employeeData = assignedEmployees.find(emp => emp.employee_email === form.employee_email);

    // Find the review data for this employee
    const reviewData = reviews.find(r => {
      const rEmail = String(unwrap(r.email) || '').toLowerCase().trim();
      const rPeriodStart = unwrap(r.period_start) || '';
      const rId = unwrap(r.id) || '';
      const empEmail = String(form.employee_email || '').toLowerCase().trim();
      const emailMatch = rEmail === empEmail && rPeriodStart === form.start_date;
      const idMatch = rId === `${empEmail}_probation_${form.start_date}` ||
                      rId === `${empEmail}_annual_${form.start_date}`;
      return emailMatch || idMatch;
    });

    // Navigate to the appropriate review page based on form type
    const routePath = form.form_type === 'annual' ? '/AnnualReview' : '/ProbationReview';
    
    navigate(routePath, {
      state: {
        formFromState: form,
        formSchemaFromState: parsedSchema,
        employeeData: employeeData,
        openManagerTab: true, // Flag to open manager review tab
        reviewData: reviewData // Pass the review data to avoid API call
      }
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Team Member Performance Reviews
        </Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={loadManagerForms} disabled={loading}>
            {loading ? <CircularProgress size={20} /> : <Refresh />}
          </IconButton>
        </Tooltip>
      </Stack>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 600 }}>Employee Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Form Type</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Period</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Employee Submission</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Manager Review</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {forms.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    No forms assigned to your team members
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              forms.map((form) => (
                <TableRow key={`${form.form_id}-${form.employee_email}`} hover>
                  <TableCell>{form.employee_name || form.employee_email}</TableCell>
                  <TableCell sx={{ textTransform: 'capitalize' }}>
                    {form.form_type || 'Probation'}
                  </TableCell>
                  <TableCell>
                    {formatDate(form.start_date)} - {formatDate(form.end_date)}
                  </TableCell>
                  <TableCell>{getStatusChip(form.status)}</TableCell>
                  <TableCell>
                    {isEmployeeSubmitted(form) ? (
                      <Chip label="Submitted" color="success" size="small" />
                    ) : (
                      <Chip label="Pending" color="warning" size="small" />
                    )}
                  </TableCell>
                  <TableCell>
                    {isManagerSubmitted(form) ? (
                      <Chip label="Completed" color="success" size="small" />
                    ) : (
                      <Chip label="Pending" color="default" size="small" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => handleOpenForm(form)}
                      disabled={form.status === 'closed' || form.status === 'archived'}
                    >
                      Review
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ManagerReviewsTable;
